import React from 'react';
import PhotoPermission, { usePhotoPermission } from './PhotoPermission';
import { API } from '../api';
import { Field, Section, Segmented, Toggle } from './ui';
import StyleTraits from './StyleTraits';

/*
 * A club's standing answers, so nobody re-picks them for every poster.
 *
 * The style memo is the odd one here: the app writes it, not the admin. Each
 * accepted poster appends a line, and the memo goes into the next prompt —
 * which is how a club's posters come to look like each other's. It is
 * editable because it is sent with every generation, so one wrong line in it
 * quietly skews everything made afterwards.
 */

/* Along the bottom edge, and only there. The top corners went when the
   reserved-space approach did: the logo is pasted on afterwards, so where it
   goes is our decision rather than something to ask the model for. */
const LOGO_LABELS = {
  none: 'No logo',
  auto: 'Wherever it fits best',
  'bottom-left': 'Bottom left',
  'bottom-centre': 'Bottom centre',
  'bottom-right': 'Bottom right',
};

const PALETTE_LABELS = {
  'club-theme': 'The club’s colours',
  custom: 'Colours I choose',
  'model-choice': 'Let it decide',
};

/** Read a picked file as a data URI, which is what the photo endpoint takes. */
const fileToDataUri = file => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error('Could not read that file'));
  r.readAsDataURL(file);
});


/**
 * Which of the club's logos a poster carries.
 *
 * "The club's default" is always an option and is what a club with one logo
 * gets without choosing. Shown as pictures rather than a dropdown of names,
 * because "Main" and "White version" mean nothing until you see them.
 */
function LogoPicker({ logos, defaultLogo, value, onChange }) {
  if (!logos || logos.length < 2) return null;
  return (
    <div className="logo-pick-row">
      <button type="button" className={`logo-pick${!value ? ' logo-pick-on' : ''}`}
        aria-pressed={!value} onClick={() => onChange('')}>
        {defaultLogo ? <img src={defaultLogo} alt="" /> : <span style={{ height: 40 }} />}
        <span>Club default</span>
      </button>
      {logos.map(l => (
        <button type="button" key={l.id}
          className={`logo-pick${value === l.id ? ' logo-pick-on' : ''}`}
          aria-pressed={value === l.id} onClick={() => onChange(l.id)}>
          <img src={l.data} alt="" />
          <span>{l.label}</span>
        </button>
      ))}
    </div>
  );
}

function PhotoLibrary({ clubId, photos, onPhotos, showToast }) {
  const [busy, setBusy] = React.useState(false);
  const [photoOk, setPhotoOk] = usePhotoPermission();
  const [error, setError] = React.useState('');
  const inputRef = React.useRef(null);

  const upload = async files => {
    if (!files || !files.length) return;
    setError('');
    setBusy(true);
    try {
      for (const file of Array.from(files).slice(0, 6)) {
        if (!/^image\//.test(file.type)) { setError(`${file.name} is not an image.`); continue; }
        // eslint-disable-next-line no-await-in-loop
        const imageData = await fileToDataUri(file);
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(`${API}/api/posters/photos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clubId, label: file.name.replace(/\.[^.]+$/, ''), imageData }),
        });
        // eslint-disable-next-line no-await-in-loop
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { setError(data.error || `Could not add ${file.name}`); continue; }
        onPhotos(prev => [data, ...prev]);
      }
      showToast('Photos added.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const rename = async (photo, label) => {
    const res = await fetch(`${API}/api/posters/photos/${photo.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ label }),
    });
    if (res.ok) onPhotos(prev => prev.map(p => (p.id === photo.id ? { ...p, label } : p)));
  };

  const remove = async photo => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove "${photo.label}" from the library?`)) return;
    const res = await fetch(`${API}/api/posters/photos/${photo.id}`, { method: 'DELETE' });
    if (res.ok) onPhotos(prev => prev.filter(p => p.id !== photo.id));
  };

  return (
    <Section
      label="Club photos"
      action={
        <button type="button" className="w3-button w3-round poster-btn"
          disabled={busy || !photoOk} title={photoOk ? undefined : 'Tick the box below first'}
          onClick={() => inputRef.current && inputRef.current.click()}>
          {busy ? 'Adding…' : 'Add photos'}
        </button>
      }>
      <p style={{ marginTop: 0, color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
        The green, the clubhouse, last year’s winners. Upload them once and pick from them on any
        poster. They are used as reference, so a phone photo is plenty — big ones are shrunk on the
        way in.
      </p>
      <PhotoPermission ok={photoOk} onChange={setPhotoOk} id="poster-photo-permission" />
      <input ref={inputRef} type="file" accept="image/*" multiple hidden
        aria-hidden="true" tabIndex={-1}
        onChange={e => upload(e.target.files)} />
      {error && <div className="poster-error" role="alert">{error}</div>}

      {photos.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--gs-text-faint)' }}>No photos yet.</p>
      ) : (
        <div className="photo-grid">
          {photos.map(p => (
            <div key={p.id} className="photo-item">
              <img src={p.imageData} alt={p.label} />
              <input className="photo-label" defaultValue={p.label} aria-label="Photo name"
                onBlur={e => rename(p, e.target.value.trim() || 'Untitled photo')} />
              <button type="button" className="photo-remove" onClick={() => remove(p)}
                aria-label={`Remove ${p.label}`}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function PosterDefaults({ clubId, config, photos, onPhotos, onSaved, showToast }) {
  const [s, setS] = React.useState(config.settings);

  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  // A different club selected upstream means different standing answers.
  React.useEffect(() => {
    setS(config.settings);
  }, [config.settings]);

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/posters/settings/${clubId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(s),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Could not save that.'); return; }
      onSaved(data);
      showToast('Defaults saved.');
    } catch (e) {
      setError(e.message || 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  const theme = config.theme || {};

  return (
    <>
      <Section label="What every poster starts from">
        <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr' }}>
          <Field label="Shape and size" htmlFor="def-size"
            help="4K 16:9 fills the club screen exactly.">
            <select id="def-size" className="w3-select" value={s.size}
              onChange={e => set('size', e.target.value)}>
              {Object.entries(config.sizes).map(([v, x]) => (
                <option key={v} value={v}>{x.label} — {v}</option>
              ))}
            </select>
          </Field>

          <Field label="Quality"
            help={`About ${((config.indicativeCents[s.size] || {})[s.quality] || 0)}c each at this size.`}>
            <Segmented ariaLabel="Default quality"
              options={config.qualities.map(q => ({ value: q, label: q.charAt(0).toUpperCase() + q.slice(1) }))}
              value={s.quality} onChange={v => set('quality', v)} />
          </Field>
        </div>

        <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr', marginTop: 'var(--gs-space-4)' }}>
          <Field label="House look" htmlFor="def-style"
            help="The starting point for every poster. It can be changed per poster.">
            <select id="def-style" className="w3-select" value={s.stylePreset}
              onChange={e => set('stylePreset', e.target.value)}>
              {config.stylePresets.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </Field>

          <Field label="Colours" htmlFor="def-palette"
            help={s.paletteMode === 'club-theme' && theme.c60
              ? `Using ${[theme.c60, theme.c30, theme.c10].filter(Boolean).join(', ')}`
              : undefined}>
            <select id="def-palette" className="w3-select" value={s.paletteMode}
              onChange={e => set('paletteMode', e.target.value)}>
              {config.paletteModes.map(m => <option key={m} value={m}>{PALETTE_LABELS[m] || m}</option>)}
            </select>
          </Field>
        </div>

        {s.paletteMode === 'custom' && (
          <div style={{ marginTop: 'var(--gs-space-4)' }}>
            <Field label="Your colours" htmlFor="def-custom"
              help="Hex values, separated by commas. Up to five.">
              <input id="def-custom" className="w3-input" placeholder="#0B4D1E, #E8D6A8, #C8952A"
                value={(s.customPalette || []).join(', ')}
                onChange={e => set('customPalette', e.target.value.split(',').map(x => x.trim()).filter(Boolean))} />
            </Field>
          </div>
        )}
      </Section>

      <Section label="Wording that goes on every poster">
        <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr' }}>
          <Field label="Standing line" htmlFor="def-standing"
            help="The line the club always puts on things.">
            <input id="def-standing" className="w3-input" placeholder="All welcome · Names in by 5:15"
              value={s.standingText} onChange={e => set('standingText', e.target.value)} />
          </Field>

          <Field label="Contact details" htmlFor="def-contact"
            help="Set small, above the strip the club screen covers.">
            <input id="def-contact" className="w3-input" placeholder="yourclub.co.nz · 09 000 0000"
              value={s.contactLine} onChange={e => set('contactLine', e.target.value)} />
          </Field>
        </div>

        <div style={{ marginTop: 'var(--gs-space-4)' }}>
          <Field label="Club logo on the poster" htmlFor="def-logo"
            help="Placed flush along the bottom edge after the poster is drawn, so it is your actual logo file rather than something a model has attempted to redraw.">
            <select id="def-logo" className="w3-select" value={s.logoPlacement}
              onChange={e => set('logoPlacement', e.target.value)}>
              {config.logoPlacements.map(p => <option key={p} value={p}>{LOGO_LABELS[p] || p}</option>)}
            </select>
          </Field>
          {s.logoPlacement !== 'none' && (
            <LogoPicker logos={config.logos} defaultLogo={config.defaultLogo}
              value={s.logoId} onChange={v => set('logoId', v)} />
          )}
        </div>
      </Section>

      <Section label="How this club’s posters should feel">
        <Field label="House style" htmlFor="def-house"
          help="Carried into every prompt. Plain words work best.">
          <textarea id="def-house" className="w3-input" style={{ minHeight: 72, resize: 'vertical' }}
            placeholder="Warm and unfussy. Real club, real people. Never corporate stock imagery."
            value={s.houseStyleNotes} onChange={e => set('houseStyleNotes', e.target.value)} />
        </Field>

        {/* The learned note is written by the app, not the admin, which is
            exactly why there has to be a way to stop using it. Off, the note
            is kept but not sent: switching back on brings back what the club
            had rather than starting it over. */}
        <div style={{ marginTop: 'var(--gs-space-4)' }}>
          <label className="poster-switch">
            <Toggle
              ariaLabel="Use the style learned from posters this club has displayed"
              checked={s.useLearnedStyle !== false}
              onChange={() => set('useLearnedStyle', s.useLearnedStyle === false)} />
            <span>Use the style learned from posters you have displayed</span>
          </label>
          <p className="poster-note" style={{ marginTop: 'var(--gs-space-2)' }}>
            {s.useLearnedStyle === false
              ? 'Off. Posters are drawn from your house style above and nothing else. What was learned is kept, not used.'
              : 'On. What this club’s chosen posters have in common is carried into each new one.'}
          </p>
        </div>

        <div style={{ marginTop: 'var(--gs-space-4)' }}>
          <Field label="Never include" htmlFor="def-avoid"
            help="Things to keep off every poster.">
            <textarea id="def-avoid" className="w3-input" style={{ minHeight: 60, resize: 'vertical' }}
              placeholder="No close-up faces. No made-up sponsor logos."
              value={s.avoidNotes} onChange={e => set('avoidNotes', e.target.value)} />
          </Field>
        </div>
      </Section>

      <StyleTraits clubId={clubId} showToast={showToast} />

      <PhotoLibrary clubId={clubId} photos={photos} onPhotos={onPhotos} showToast={showToast} />

      {error && <div className="poster-error" role="alert">{error}</div>}

      <div style={{ marginTop: 'var(--gs-space-4)' }}>
        <button type="button" className="w3-button w3-round poster-btn-primary"
          disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save defaults'}
        </button>
      </div>
    </>
  );
}

export default PosterDefaults;
